<template>
  <vetster-common-layout class="min-h-[calc(100vh-64px)]">
    <!-- Recommend vet -->
    <vetster-recommend-vet
      v-if="isRecommendVetExperiment"
      :locale="locale"
      :country="country"
      :loading="loading"
      @location-selected="handleLocationSelected"
      @filter-and-search="filterAndSearch"
    />

    <!-- Guided search -->
    <div
      v-else
      class="flex flex-col w-full relative overflow-y-auto md:overflow-y-visible md:max-w-7xl md:mx-auto md:px-standard pb-8"
    >
      <!-- Filter buttons -->
      <vetster-search-button-filters
        :species-treated="speciesTreated"
        class="pt-6 lg:pt-12 w-full"
        @toggle-filters="toggleFilters"
        @refresh-search="filterAndSearch"
        @change-location="handleToggleFilters"
      />

      <div class="relative mt-6 lg:mt-4 px-standard lg:px-0">
        <transition name="fade">
          <div
            v-if="!loading"
            class="flex flex-col"
          >
            <vetster-empty-search
              v-if="!hasCity"
              :header="$t('search.missing-location-information')"
              :subheader="$t('search.please-enter-your-location-area')"
            >
              <template #body>
                <vetster-button
                  variant="white"
                  class="mt-8 w-full"
                  @click="openLocationSlideOver"
                >
                  <span class="text-sm font-bold">
                    {{ $t('search.add-your-location') }}
                  </span>
                </vetster-button>
              </template>
            </vetster-empty-search>
            <vetster-empty-search
              v-else-if="!hasResults"
              :header="$t('search.lets-find-the-right-care-for-your-pet')"
              :subheader="$t('search.sometimes-we-cant-find-an-immediate-fit')"
            >
              <template #body>
                <vetster-livechat-cta
                  event-parent="guided-search"
                  event-child="chat-cta"
                  class="mt-1"
                >
                  <template #store-icons>
                    <div class="mt-6 flex justify-center items-center">
                      <div class="flex flex-1 flex-col w-full justify-center items-center sm:items-end pr-1">
                        <vetster-mobile-app-buttons
                          button-size="w-32"
                          :apple-app-store-url="appStore.url"
                          :google-play-url="playStore.url"
                        />
                      </div>
                      <div class="hidden sm:flex sm:flex-1 sm:pl-2">
                        <img
                          src="https://images.vetster.com/photos/livechat_qr_code.jpeg?ik-s=81d9166c8b7f0c8b03a8536700afdd184aaf5cc0"
                          :alt="$t('mobile-app.livechat-qr-code')"
                          class="size-24"
                        >
                      </div>
                    </div>
                  </template>
                </vetster-livechat-cta>
                <div class="flex flex-col gap-5 mt-6">
                  <a
                    href="#"
                    class="leading-4 mx-auto inline-block text-ocean-950 underline underline-offset-2 text-sm lg:text-base"
                    @click.prevent="clearFilters"
                  >
                    {{ $t('search.clear-filters') }}
                  </a>
                </div>
              </template>
            </vetster-empty-search>
            <div v-else>
              <vetster-search-banner class="mb-4 px-standard" />
              <vetster-search-results
                :results="results"
                :timezone="timezone"
                :max-results="resultsMax"
                class="mb-4 lg:mb-0"
                @selected="selectResult"
              />
            </div>
          </div>
        </transition>
      </div>

      <vetster-recommend-vet-banner v-if="showRecommendVetBanner" />
    </div>

    <vetster-partner-details
      v-if="resultSelected"
      ref="partnerSlideOver"
      :result="resultSelected"
      :search-params="params"
      :timeslot-selected="timeslotSelected"
      :timezone="timezone"
      :is-recommended="showRecommendVetOnDetails"
      :show-recommend-vet-card="isRecommendVetExperiment"
      @close="handleClose"
      @navigate-to-guided-search="navigateToGuidedSearch"
    />

    <!-- Filters -->
    <vetster-slide-over
      ref="filters"
      container-classes="lg:h-[80vh]"
      size="lg"
      @close="hideFilters"
      @after-enter="afterEnterFilters"
    >
      <template #header>
        <vetster-slide-over-common-heading
          :header="$t('search.filters-label')"
          @close="closeFilters"
        />
      </template>

      <template #content>
        <vetster-search-filters
          ref="searchFilters"
          :filters="filters"
          :locale="locale"
          :countries="country"
          @location-selected="handleLocationSelected"
        />
      </template>

      <template #footer>
        <div class="py-4 bg-white px-5 w-full flex space-x-2 border-t border-neutral-200">
          <vetster-button
            data-testid="search-clear-btn"
            variant="outlineOcean"
            size="fixed"
            class="flex-1"
            @click="clearFilters"
          >
            {{ $t('search.clear') }}
          </vetster-button>
          <vetster-button
            data-testid="search-apply-btn"
            variant="ocean"
            size="fixed"
            class="flex-1"
            @click="filterAndSearch"
          >
            {{ $t('search.apply') }}
          </vetster-button>
        </div>
      </template>
    </vetster-slide-over>

    <vetster-location-slide-over
      ref="locationSlideOver"
      :locale="locale"
      :country="country"
      @location-selected="filterAndSearch"
    />

    <vetster-page-spinner
      :show="loading"
      is-rebranded
      class="!fixed !bg-white"
    />
  </vetster-common-layout>
</template>

<script>
import clone from 'lodash/clone';
import concat from 'lodash/concat';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import size from 'lodash/size';
import slice from 'lodash/slice';
import { DateTime } from 'luxon';
import { getCurrentInstance } from 'vue';
import { mapGetters } from 'vuex';

import VetsterEmptySearch from '@/brand-components/GuidedSearch/VetsterEmptySearch';
import VetsterLocationSlideOver from '@/brand-components/GuidedSearch/VetsterLocationSlideOver';
import VetsterPartnerDetails from '@/brand-components/GuidedSearch/VetsterPartnerDetails';
import VetsterRecommendVet from '@/brand-components/GuidedSearch/VetsterRecommendVet';
import VetsterRecommendVetBanner from '@/brand-components/GuidedSearch/VetsterRecommendVetBanner';
import VetsterSearchBanner from '@/brand-components/GuidedSearch/VetsterSearchBanner';
import VetsterSearchButtonFilters from '@/brand-components/GuidedSearch/VetsterSearchButtonFilters';
import VetsterSearchFilters from '@/brand-components/GuidedSearch/VetsterSearchFilters';
import VetsterSearchResults from '@/brand-components/GuidedSearch/VetsterSearchResults';
import VetsterCommonLayout from '@/brand-components/VetsterCommonLayout';
import VetsterSlideOver from '@/brand-components/VetsterSlideOver';
import VetsterSlideOverCommonHeading from '@/brand-components/VetsterSlideOverCommonHeading';
import VetsterLivechatCTA from '@/components/VetsterLivechatCTA';
import VetsterMobileAppButtons from '@/components/VetsterMobileAppButtons';
import VetsterPageSpinner from '@/components/VetsterPageSpinner';
import useAppointmentDatetimeFormat from '@/composables/use-appointment-datetime-format';
import useBrowserApiHelpers from '@/composables/use-browser-api-helpers';
import useCheckoutHelpers from '@/composables/use-checkout-helpers';
import useErrorHandling from '@/composables/use-error-handling';
import useSearchData from '@/composables/use-search-data';
import useSearchPets from '@/composables/use-search-pets';
import SearchType from '@/enums/SearchType';
import SpeciesTreatedType from '@/enums/SpeciesTreatedType';
import {
  getAssetUrl,
  slugify,
} from '@/helpers/url';
import LocalizesRoutes from '@/mixins/LocalizesRoutes';
import ManagesSearchCookies from '@/mixins/ManagesSearchCookies';
import TrackSearches from '@/mixins/TrackSearches';

export default {
  name: 'VetsterSearch',

  components: {
    VetsterCommonLayout,
    VetsterEmptySearch,
    'vetster-livechat-cta': VetsterLivechatCTA,
    VetsterLocationSlideOver,
    VetsterMobileAppButtons,
    VetsterPageSpinner,
    VetsterPartnerDetails,
    VetsterRecommendVet,
    VetsterSearchBanner,
    VetsterSearchButtonFilters,
    VetsterSearchFilters,
    VetsterSearchResults,
    VetsterSlideOver,
    VetsterSlideOverCommonHeading,
    VetsterRecommendVetBanner,
  },

  mixins: [
    LocalizesRoutes,
    ManagesSearchCookies,
    TrackSearches,
  ],

  props: {
    country: {
      type: String,
      required: true,
    },
    filters: {
      type: [Object, Array],
      default: () => ({}),
    },
    isRecommended: {
      type: Boolean,
      default: false,
    },
    locale: {
      type: String,
      default: null,
    },
    mobileApps: {
      type: [Object, Array],
      required: true,
    },
  },

  setup(props) {
    const root = getCurrentInstance().proxy.$root;
    const { getAppointmentDate } = useAppointmentDatetimeFormat();
    const { localStorageAvailable } = useBrowserApiHelpers();
    const { isSearchUuidParameterIgnored } = useCheckoutHelpers(root);
    const { choosePet } = useSearchPets();
    const {
      isRecommendVetBannerExperiment,
      isRecommendVetExperiment,
      searchConcernLimit,
    } = useSearchData(props.isRecommended);

    const { getError } = useErrorHandling();

    return {
      choosePet,
      getAppointmentDate,
      getError,
      isRecommendVetBannerExperiment,
      isRecommendVetExperiment,
      isSearchUuidParameterIgnored,
      localStorageAvailable,
      searchConcernLimit,
    };
  },

  data() {
    return {
      fallbackResults: false,
      loading: true,
      navigationIndex: 0,
      params: {},
      results: [],
      resultsMax: 6,
      resultSelected: null,
      showStats: false,
      timeslotSelected: null,
      timezone: DateTime.local().zoneName,
    };
  },

  computed: {
    ...mapGetters([
      'getUser',
      'isGlobalUser',
    ]),
    ...mapGetters('pets', ['hasPets']),
    ...mapGetters('search', [
      'getInputs',
      'getShowFilters',
      'getTempInputs',
      'isVcprSearch',
    ]),
    ...mapGetters('subscriptions', ['canPurchaseSubscriptionPlan']),

    appStore() {
      return this.mobileApps.apple;
    },

    hasCity() {
      return !!this.getInputs.city;
    },

    hasConcernsSelected() {
      return size(this.getInputs.concerns) > 0;
    },

    hasFewVcprResults() {
      return this.hasResults
        && this.results.length < this.resultsMax
        && this.isVcprSearch;
    },

    hasOneValidRecommendVetResult() {
      return size(this.validRecommendVetResults) === 1;
    },

    hasResults() {
      return size(this.results) > 0;
    },

    hasValidRecommendVetResults() {
      return size(this.validRecommendVetResults) > 0;
    },

    playStore() {
      return this.mobileApps.google;
    },

    shouldFallbackSearch() {
      return this.params?.uuid && !this.hasResults;
    },

    showRecommendVetBanner() {
      return this.isRecommendVetBannerExperiment
        && this.hasValidRecommendVetResults
        && size(this.validRecommendVetResults) > 1;
    },

    showRecommendVetOnDetails() {
      return this.isRecommendVetExperiment
        || (this.isRecommendVetBannerExperiment && this.hasOneValidRecommendVetResult)
        || this.showStats;
    },

    speciesTreated() {
      if (size(this.getInputs.species_treated)) {
        return this.getInputs.species_treated[0];
      }

      return null;
    },

    validRecommendVetResults() {
      return this.results.filter((result) => result.is_in_state);
    },
  },

  watch: {
    getShowFilters(value) {
      if (value) {
        this.$refs.filters.open();

        this.$analytics.track({
          eventName: `select_vet_${value}_click`,
          parent: 'guided-search',
          child: 'select-vet',
          version: '2.0',
        });
      }
    },

    loading(newVal) {
      if (!newVal && !this.hasCity) {
        this.openLocationSlideOver();
      }
    },

    params: {
      handler(newVal) {
        this.$store.commit('search/setIsVcprSearch', newVal.is_vcpr_eligible);
      },
      deep: true,
      immediate: true,
    },
  },

  async mounted() {
    this.$store.commit('search/setShowFilters', false);
    await this.fetchYearlySubscriptionPlan();
    await this.setInitialConcerns();
    await this.setInitialSpeciesTreated();
    this.$store.commit('search/takeSnapshot');

    if (this.isRecommendVetExperiment) {
      // loading defaults to true for guided search to avoid layout shifting
      this.loading = false;
    } else {
      await this.guidedSearch();
    }
  },

  methods: {
    afterEnterFilters() {
      document.querySelector('.mapboxgl-ctrl-geocoder--input').focus();
    },

    async clearFilters() {
      await this.$emit('clear-filters');
      await this.setInitialSpeciesTreated();
      this.setDateFrom(null);

      this.$nextTick(() => {
        this.closeFilters();
        this.search();
      });
    },

    closeFilters() {
      this.$refs.filters.close();
      this.hideFilters();
    },

    hideFilters() {
      this.$store.commit('search/setShowFilters', false);
    },

    async fallbackSearch(searchUuid) {
      this.loading = true;
      this.trackSearchLoad(SearchType.GuidedSearchFallback, '2.0');
      await this.$api.post('p/search/guided/fallback', {
        search_uuid: searchUuid,
      })
        .then(
          (response) => {
            this.errors = '';
            if (response.data?.meta) {
              this.meta = response.data.meta;
            }
            if (response.data?.links) {
              this.links = response.data.links;
            }
            if (response.data?.search) {
              this.params = this.replaceParamsFromWebApp(response.data.search);
            }

            if (this.hasFewVcprResults) {
              this.results = concat(
                this.results,
                slice(
                  response.data.data,
                  0,
                  this.resultsMax - this.results.length,
                ),
              );
            } else {
              this.results = response.data.data;
            }
          },
          (error) => {
            this.errors = this.getError(error);
            this.loading = false;
          },
        )
        .finally(() => {
          this.fireSelectVetLoadEvent(this.results);
          this.loading = false;
        });
    },

    async fetchYearlySubscriptionPlan() {
      if (this.canPurchaseSubscriptionPlan) {
        try {
          await this.$store.dispatch('subscriptionPlans/fetchSubscriptionPlan');
        } catch {
          //
        }
      }
    },

    fireAddressEvent(address) {
      this.$analytics.track({
        eventName: 'select_location_click',
        parent: 'guided-search',
        child: 'filter-menu',
        value: address,
        version: '2.0',
      });
    },

    fireSelectVetLoadEvent(results) {
      const resultsCount = size(results);

      this.$analytics.track({
        eventName: 'select_vet_load',
        parent: 'guided-search',
        child: 'select-vet',
        count: resultsCount,
        search_id: this.getInputs.search_uuid,
        version: '2.0',
      });
    },

    async filterAndSearch() {
      const tempInputs = this.getTempInputs;
      this.fireDateEvent(tempInputs.date_from);
      this.setDateFrom(tempInputs.date_from);
      await this.setConcerns(tempInputs.concerns);
      this.fireSpeciesTreatedEvent(tempInputs.species_treated);
      if (this.hasPets && tempInputs.pet_uuid) {
        this.firePetEvent(tempInputs.pet_uuid);
        await this.choosePet(this.findPet(tempInputs.pet_uuid));
      } else {
        await this.setSpeciesTreated(tempInputs.species_treated);
      }
      await this.setAddress();
      await this.$store.commit('search/setInputs', {
        search_uuid: null,
        search_result_uuid: null,
      });
      this.$nextTick(() => {
        this.closeFilters();
        this.search();
      });
    },

    fireConcernsEvent(concerns) {
      // Verify if new concerns have been selected
      if (!isEqual(this.getInputs.concerns, concerns)) {
        this.$analytics.track({
          eventName: 'select_concerns_click',
          parent: 'guided-search',
          child: 'filter-menu',
          value: concerns,
          version: '2.0',
        });
      }
    },

    fireDateEvent(date) {
      const newDate = DateTime.fromISO(date);
      const oldDate = DateTime.fromISO(this.getInputs.date_from);

      // Verify if a new date has been selected
      if ((date !== this.getInputs.date_from) && !newDate.hasSame(oldDate, 'day')) {
        this.$analytics.track({
          eventName: 'select_date_click',
          parent: 'guided-search',
          child: 'filter-menu',
          value: newDate.toISODate(),
          version: '2.0',
        });
      }
    },

    fireSpeciesTreatedEvent(species) {
      // Verify if a new species has been selected
      if (!isEqual(this.getInputs.species_treated, [species])) {
        this.$analytics.track({
          eventName: 'select_species_click',
          parent: 'guided-search',
          child: 'filter-menu',
          value: species,
          version: '2.0',
        });
      }
    },

    firePetEvent(petUuid) {
      if (!isEqual(this.getInputs.pet_uuid, petUuid)) {
        this.$analytics.track({
          eventName: 'select_pet_click',
          parent: 'guided-search',
          child: 'filter-menu',
          value: petUuid,
          version: '2.0',
        });
      }
    },

    generateUrl(isGuidedSearch = true) {
      let filters = this.localStorageAvailable()
        ? JSON.parse(localStorage.searchParams)
        : null;

      if (!filters) {
        filters = this.getInputs;
      }

      const url = `${
        isGuidedSearch ? this.guidedSearchUrl : this.localizeUrl('s?designation=VET')
      }/${slugify(
        `Vets in ${[filters.city, filters.province, filters.country]
          .filter(Boolean)
          .join(', ')}`,
      )}`;
      filters = omit(filters, [
        'currency',
        'date_to',
        'platform',
        'time_from',
        'time_to',
        'uuid',
      ]);
      const queryString = Object.keys(filters)
        .map((key) => {
          if (key === 'date_from') {
            filters[key] = DateTime.fromISO(filters[key], {
              zone: filters.timezone,
            }).toISODate();
          }

          return `${encodeURIComponent(key)}=${encodeURIComponent(
            filters[key],
          )}`;
        })
        .join('&');

      return `${url}?${queryString}`;
    },

    getAssetUrl,

    getConcerns() {
      if (this.hasConcernsSelected) {
        return this.getInputs.concerns;
      }

      return this.getConcernsFromCookie();
    },

    getPayload() {
      const payload = omit(clone(this.getInputs), [
        'areas_of_interest',
        'date_to',
        'full_name',
        'time_from',
        'time_to',
      ]);

      payload.species = this.speciesTreated;

      const speciesTreatedType = SpeciesTreatedType.fromPetSpecies(
        payload.species,
      );
      if (speciesTreatedType) {
        payload.species = speciesTreatedType;
        payload.species_treated = [speciesTreatedType];
      }

      payload.pet_uuid = this.getInputs.pet_uuid || this.getPetFromCookie()?.uuid || '';

      payload.concerns = this.getConcerns();
      if (
        payload.concerns
        && payload.concerns.length === 1
        && (payload.concerns[0] === '')
      ) {
        delete payload.concerns;
      }

      if (payload.sort_by === '' || payload.sort_order === '') {
        delete payload.sort_by;
        delete payload.sort_order;
      }

      payload.platform = this.getInputs.platform;
      payload.is_global = this.isGlobalUser || this.getUser.isGlobal();

      return payload;
    },

    getRecommendVetPayload() {
      return omit(this.getPayload(), [
        'concerns',
        'date_from',
        'pet_uuid',
        'search_result_uuid',
        'search_uuid',
        'temp_date_from',
      ]);
    },

    handleClose() {
      setTimeout(() => {
        this.resultSelected = null;
      }, 500);
    },

    handleToggleFilters() {
      if (this.hasCity) {
        this.toggleFilters();
      } else {
        this.openLocationSlideOver();
      }
    },

    openLocationSlideOver() {
      this.$refs.locationSlideOver.open();
    },

    async selectResult(result, timeslot = null) {
      this.$analytics.track({
        eventName: 'select_vet_click',
        parent: 'guided-search',
        child: 'select-vet',
        version: '2.0',
      });

      this.timeslotSelected = timeslot;
      this.resultSelected = result;

      if (result) {
        this.$nextTick(() => {
          this.$refs.partnerSlideOver.open();
        });
      }
    },

    search() {
      if (this.isRecommendVetExperiment) {
        this.recommendedSearch();
      } else {
        this.guidedSearch();
      }
    },

    async guidedSearch(
      values = {
        skipHashUpdate: false,
        payload: this.getPayload(),
      },
    ) {
      const { skipHashUpdate, payload } = values;
      const hasResultsRestored = !!this.getInputs.search_uuid && !this.isSearchUuidParameterIgnored;
      this.loading = true;
      this.results = [];
      this.fallbackResults = [];

      const request = hasResultsRestored
        ? this.$api.get(`p/search/guided/${this.getInputs.search_uuid}`)
        : this.$api.post('p/search/guided', payload);

      return request
        .then(async (response) => {
          this.errors = '';
          this.results = response.data.data;
          this.meta = response.data.meta;
          this.links = response.data.links;

          this.params = hasResultsRestored
            ? this.replaceParamsFromWebApp(response.data.search)
            : response.data.search;

          if (hasResultsRestored) {
            await this.$store.commit('search/setInputs', {
              ...omit({ ...this.params }, [
                'is_global',
                'is_vcpr_eligible',
                'parent_uuid',
                'results',
              ]),
              species_treated: [this.params.species_treated],
            });

            if (this.getInputs.search_result_uuid) {
              await this.openLastViewedPartner();
            }
          }

          await this.$store.commit('search/setInputs', {
            search_uuid: this.params.uuid,
          });

          if (!skipHashUpdate) {
            window.history.replaceState(
              payload,
              this.$t('search.vetster-search-results'),
              this.generateUrl(response.data.search),
            );
          }
        })
        .catch(async (error) => {
          if (this.getInputs.search_uuid) {
            await this.$store.commit('search/setInputs', { search_uuid: null });
          }
          this.errors = this.getError(error);
        })
        .finally(async () => {
          this.fireSelectVetLoadEvent(this.results);
          if (this.shouldFallbackSearch) {
            await this.fallbackSearch(this.params?.uuid);
          }

          this.loading = false;
        });
    },

    async navigateToGuidedSearch() {
      this.resultSelected = null;
      this.$store.commit('search/setInputs', {
        search_uuid: null,
        search_result_uuid: null,
      });
      await this.guidedSearch();
      if (this.hasOneValidRecommendVetResult && this.isRecommendVetExperiment) {
        this.showStats = true;
      }
      this.$store.commit('search/setHideRecommendVet', true);
    },

    async recommendedSearch() {
      this.loading = true;
      this.errors = '';
      let recommendedVet = [];
      let response = {};

      try {
        response = await this.$api.post('p/search/recommend', this.getRecommendVetPayload());

        if (response.status !== 204) {
          const result = response.data.data;

          this.meta = response.data.meta;
          this.links = response.data.links;
          this.params = response.data.search;
          recommendedVet = [result];

          await this.$store.commit('search/setInputs', {
            search_uuid: this.params.uuid,
          });

          await this.selectResult(result);
        }
      } catch (error) {
        this.errors = this.getError(error);
      } finally {
        if (response.status === 204 || this.errors) {
          this.navigateToGuidedSearch();
        } else {
          this.fireSelectVetLoadEvent(recommendedVet);
          this.loading = false;
        }
      }
    },

    setDateFrom(date) {
      this.$store.commit('search/setDates', { date_from: date });
      this.$store.commit('search/setInputs', { date_from: date });
    },

    async setInitialConcerns() {
      const concerns = this.getConcernsFromCookie();

      if (!this.hasConcernsSelected && size(concerns)) {
        await this.$store.commit('search/setInputs', { concerns });
      }
    },

    async setInitialSpeciesTreated() {
      if (this.isClient && this.hasPets) {
        await this.choosePet(this.getPetFromCookie() || this.getPets[0]);

        return;
      }

      if (!size(this.getInputs?.species_treated)) {
        await this.setSpeciesTreated(SpeciesTreatedType.Dog);
      }
    },

    async setSpeciesTreated(speciesTreated) {
      if (size(speciesTreated)) {
        await this.$store.commit('search/setInputs', {
          species_treated: this.setSpeciesTreatedCookie(speciesTreated),
        });
      }
    },

    handleLocationSelected(data) {
      this.$store.commit('search/setTempInputs', data);
    },

    /**
     * Override PHP SearchResource's payload, setting the pet or concerns
     * selected in vetster-web-app's checkout flow.
     */
    replaceParamsFromWebApp(params) {
      const pet = this.getPetFromCookie();
      const concerns = this.getConcernsFromCookie();
      let replace = {};

      if (pet && pet.uuid !== params.pet_uuid) {
        replace = {
          pet_uuid: pet.uuid,
          species_treated: pet.species,
        };
      }

      if (size(concerns)) {
        replace.concerns = concerns;
      }

      // When search filters have changed, don't set ?search_uuid=.
      if (size(replace)) {
        replace.search_uuid = null;
        replace.uuid = null;
      }

      return {
        ...params,
        ...replace,
      };
    },

    async setAddress() {
      const { city, province, country } = this.getTempInputs;
      const tempAddress = { city, province, country };
      // Verify if a new address has been selected
      const addressChanged = tempAddress
        && ['city', 'province', 'country'].some((prop) => tempAddress?.[prop] !== this.getInputs[prop]);

      if (addressChanged) {
        this.fireAddressEvent(tempAddress);
        await this.$store.commit('search/setInputs', tempAddress);
      }
    },

    async setConcerns(concernsSelected) {
      const concerns = this.filterValidConcerns(concernsSelected).slice(0, this.searchConcernLimit);

      this.fireConcernsEvent(concerns);
      this.setConcernsCookie(concerns);
      await this.$store.commit('search/setInputs', { concerns });
    },

    toggleFilters() {
      this.$store.commit('search/takeSnapshot');
      this.$store.commit('search/setShowFilters', 'filter_text');
    },

    async openLastViewedPartner() {
      const previousResult = this.results.find((item) => item.uuid === this.getInputs.search_result_uuid);

      if (previousResult) {
        await this.selectResult(previousResult);
      }
    },
  },
};
</script>
